﻿import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import { motion } from 'framer-motion';
import styled from 'styled-components';

import Accordion from '../../UI/Accordion';
import Loader from '../../UI/Loader';
import ErrorMessage from '../../UI/ErrorMessage';
import RawHtml from '../../UI/RawHtml';
import PossessionHeading from './Components/PossessionHeading';
import PossessionInfo from './Components/PossessionInfo';
import PossessionCustomHeading from './Components/PossessionCustomHeading'

import {loadStartPageDataAction} from './State/actions';
import {getEpiData} from '../../Utils/epiHelpers';
import {getPremiumLabel} from '../../DataHelpers/lfDataHelpers';

import { mediaQuery } from '../../Style/cssHelpers';
import definitions from '../../Style/LfStyleDefinitions';


class SignedInStartPage extends React.Component {
    static get propTypes() {
        return {
            mountRootId: PropTypes.string.isRequired
        };
    }

    constructor(props) {
        super(props);

        this.heading = getEpiData('heading', this.props.mountRootId);
        this.preamble = getEpiData('preamble', this.props.mountRootId);
        this.futureInsurancesHeading = getEpiData('future-insurances-heading', this.props.mountRootId);
        this.futureInsurancesPreamble = getEpiData('future-insurances-preamble', this.props.mountRootId);
    }

    componentDidMount() {
        this.props.loadData();
    }

    render() {
        const hasNoPossessions = !this.props.data.possessions || !this.props.data.possessions.length;

        if (this.props.error.hasErrored) {
            return <ErrorMessage
                        message={
                            `Ooops! Ett fel uppstod vid hämtningen: ${typeof this.props.error === 'string'
                                ? this.props.error
                                : this.props.error.message}`
                        }
                    />;
        }

        if (this.props.isLoading) {
            return (
                <React.Fragment>
                    <Loader color={definitions.colors.blueBackground}/>
                </React.Fragment>
            );
        }
        if (hasNoPossessions) {
            return (
                <React.Fragment>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                    >
                        <HeadingWrapper>
                            <h3>{this.heading}</h3>
                            Du har just nu inga tecknade försäkringar.
                        </HeadingWrapper>
                    </motion.div>
                </React.Fragment>
            );
        }

        // custom header for displaying on My Pages / My Possessions
        const CustomHeader = ({ text, value, isCurrent, date, heading }) => {
            return(
                <div className="possessions-custom-header" data-lf-accordion>                   
                    <PossessionCustomHeading text={text} value={value} isCurrent={isCurrent} date={date} heading={heading} />                  
                </div>
              )
           }

        const PossessionListing = ({ possessions, current }) =>
            possessions.map((insurance, i) => (
                <Accordion
                    key={`possession-${i}`}
                    title={<PossessionHeading possession={insurance} />}
                    labelRight={{ text: getPremiumLabel(insurance.premiumPeriod), value: insurance.premium }}
                    align={'right'}
                    customHeader={
                        <CustomHeader
                            heading={insurance.displayName}
                            text={getPremiumLabel(insurance.premiumPeriod)}
                            value={insurance.premium}
                            isCurrent={current}
                            date={insurance.fromDate}
                        />
                    }
                >
                    <PossessionInfo possession={insurance} />
                </Accordion>
            ));

        return (
            <React.Fragment>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                >
                    {this.props.data.currentPossessions.length > 0 &&
                        <div>
                            <ColumnContainer>
                                <Column>
                                    {(this.heading || this.preamble) && (
                                        <HeadingWrapper>
                                            <h3>{this.heading}</h3>
                                            <RawHtml>
                                                {this.preamble}
                                            </RawHtml>
                                        </HeadingWrapper>
                                    )}
                                </Column>
                                <Column>
                                    <PremiumSummary>
                                        <strong style={{ marginRight: 10 }}>Summa månadspris:</strong> {this.props.data.currentPremiumTotal} kr
                                    </PremiumSummary>
                                </Column>
                            </ColumnContainer>
                            <PossessionListing possessions={this.props.data.currentPossessions} current={true} />
                        </div>
                    }

                    {this.props.data.futurePossessions.length > 0 &&
                        <div>
                            <Spacer />
                            <HeadingWrapper>
                                <h3>{this.futureInsurancesHeading}</h3>
                                <RawHtml>
                                    {this.futureInsurancesPreamble}
                                </RawHtml>
                            </HeadingWrapper>
                            <PossessionListing possessions={this.props.data.futurePossessions} current={false} />
                        </div>
                    }
                </motion.div>
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        data: state.startPageData,
        hasErrored: state.pageHasErrored,
        error: state.pageError,
        isLoading: state.pageIsLoading,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        loadData: () => dispatch(loadStartPageDataAction())
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(SignedInStartPage);

// styles

const Spacer = styled.div`
    height: 20px;
    ${mediaQuery.xsmall(`
        height: 10px;
    `)}
`;
const HeadingWrapper = styled.div`
    padding: 17px 17px 0px 17px;

    ${mediaQuery.small(`
        padding-bottom: 17px;
    `)}

    h3 {
        font-size: 28px;
        margin-bottom: 10px;

        ${mediaQuery.xsmall(`
            font-size: 20px;
            margin-bottom: 5px;
        `)}
    }

    p {
        margin-top: 0;
    }
    p:last-child {
        margin-bottom: 0;
    }
`;

const ColumnContainer = styled.div`
    width: 100%;
`;

const Column = styled.div`

`;

const PremiumSummary = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 10px;
`;
